import React from 'react';
import { cn } from '../../utils/cn';

interface PercentileRankProps {
  percentile: string;
  isLoading?: boolean;
  className?: string;
}

/**
 * PercentileRank - Displays the user's percentile ranking with emoji
 *
 * Shows different emojis based on percentile tier:
 * - 🥇 Top 10% (gold medal)
 * - 🥈 Top 25% (silver medal)
 * - 🥉 Top 50% (bronze medal)
 * - 🎯 Top 75% (target)
 * - 🦍 Top 100% (ape)
 *
 * Special cases:
 * - Loading state: "Calculating rank..."
 * - Anonymous/guest users: "🏆 Top ❓%"
 */
export const PercentileRank: React.FC<PercentileRankProps> = ({
  percentile,
  isLoading,
  className
}) => {
  if (isLoading) {
    return (
      <div className={cn("text-center animate-pulse", className)}>
        <p className="text-sm text-muted-foreground">Calculating rank...</p>
      </div>
    );
  }

  // Anonymous users or users without percentile data
  if (percentile === '?') {
    return (
      <div className={cn("text-center", className)}>
        <p className="text-sm font-medium text-foreground">
          🏆 Top ❓%
        </p>
      </div>
    );
  }

  const numericPercentile = parseFloat(percentile);

  // Handle invalid percentile values
  if (isNaN(numericPercentile)) {
    return null;
  }

  const emoji = getEmojiForPercentile(numericPercentile);
  const roundedPercentile = Math.round(numericPercentile);
  const displayPercentile = Math.min(roundedPercentile, 100);

  return (
    <div className={cn("text-center", className)}>
      <p className="text-sm font-medium text-foreground">
        {emoji} You are in the Top {displayPercentile}% of all players!
      </p>
    </div>
  );
};

/**
 * Get emoji based on percentile tier
 */
export function getEmojiForPercentile(percentile: number): string {
  if (percentile <= 10) return '🥇'; // Gold medal - Top 10%
  if (percentile <= 25) return '🥈'; // Silver medal - Top 25%
  if (percentile <= 50) return '🥉'; // Bronze medal - Top 50%
  if (percentile <= 75) return '🎯'; // Target - Top 75%
  return '🦍'; // Ape - Everyone else
}
